import { RingBuffer } from './ring-buffer';
import type { NetworkEntry } from './types';

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-api-key'];

export class NetworkBuffer {
  private buffer: RingBuffer<NetworkEntry>;
  private originalFetch: typeof window.fetch | null = null;
  private originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXhrSend: typeof XMLHttpRequest.prototype.send | null = null;
  private installed = false;

  constructor(bufferSize = 50) {
    this.buffer = new RingBuffer(bufferSize);
  }

  install(): void {
    if (this.installed) return;
    this.installed = true;
    this.patchFetch();
    this.patchXhr();
  }

  private patchFetch(): void {
    this.originalFetch = window.fetch.bind(window);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const origFetch = this.originalFetch;

    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const method = init?.method?.toUpperCase() ?? 'GET';
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const start = Date.now();

      try {
        const response = await origFetch(input, init);
        const entry: NetworkEntry = {
          ts: new Date(start).toISOString(),
          method,
          url: self.sanitizeUrl(url),
          status: response.status,
          duration_ms: Date.now() - start,
        };

        const reqId = response.headers.get('x-request-id');
        if (reqId) entry.request_id = reqId;

        if (response.status >= 400) {
          entry.error_type = `HTTP ${response.status}`;
          entry.response_status_text = response.statusText;
        }

        self.buffer.push(entry);
        return response;
      } catch (err) {
        self.buffer.push({
          ts: new Date(start).toISOString(),
          method,
          url: self.sanitizeUrl(url),
          duration_ms: Date.now() - start,
          error_type: err instanceof TypeError ? 'network_error' : 'fetch_error',
        });
        throw err;
      }
    };
  }

  private patchXhr(): void {
    this.originalXhrOpen = XMLHttpRequest.prototype.open;
    this.originalXhrSend = XMLHttpRequest.prototype.send;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const origOpen = this.originalXhrOpen;
    const origSend = this.originalXhrSend;

    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: any[]
    ) {
      (this as any).__bugReportMeta = {
        method: method.toUpperCase(),
        url: typeof url === 'string' ? url : url.href,
      };
      return origOpen.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const meta = (this as any).__bugReportMeta;
      if (!meta) {
        return origSend.call(this, body);
      }
      const start = Date.now();

      this.addEventListener('loadend', function () {
        const entry: NetworkEntry = {
          ts: new Date(start).toISOString(),
          method: meta.method,
          url: self.sanitizeUrl(meta.url),
          status: this.status || undefined,
          duration_ms: Date.now() - start,
        };

        const reqId = this.getResponseHeader('x-request-id');
        if (reqId) entry.request_id = reqId;

        if (this.status >= 400) {
          entry.error_type = `HTTP ${this.status}`;
          entry.response_status_text = this.statusText;
        } else if (this.status === 0) {
          entry.error_type = 'network_error';
        }

        self.buffer.push(entry);
      });

      return origSend.call(this, body);
    };
  }

  private sanitizeUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      for (const key of SENSITIVE_HEADERS) {
        parsed.searchParams.delete(key);
      }
      parsed.searchParams.delete('token');
      parsed.searchParams.delete('api_key');
      return parsed.pathname + parsed.search;
    } catch {
      return url;
    }
  }

  getAll(): NetworkEntry[] {
    return this.buffer.getAll();
  }

  clear(): void {
    this.buffer.clear();
  }

  destroy(): void {
    if (!this.installed) return;
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
    if (this.originalXhrOpen) {
      XMLHttpRequest.prototype.open = this.originalXhrOpen;
      this.originalXhrOpen = null;
    }
    if (this.originalXhrSend) {
      XMLHttpRequest.prototype.send = this.originalXhrSend;
      this.originalXhrSend = null;
    }
    this.installed = false;
  }
}
